import { Box, Collapse, Stack, StackProps } from '@mui/material';
import { TypographyVariant } from '@mui/material/styles';
import { ReactNode, useState } from 'react';
import useTeceoStyles from '../../useTeceoStyles';
import ExpandableTitle from './components/ExpandableTitle';
import ExpandableToggle from './components/ExpandableToggle';
import useStyles from './expandable.styles';

interface ExpandableProps {
  children: ReactNode;
  customHeader?: ReactNode;
  title?: string;
  important?: boolean;
  lock?: boolean;
  initializeOpened?: boolean;
  titleVariant?: TypographyVariant;
  classes?: { title: string };
  reverse?: boolean;
  value?: boolean;
  onClick?: (value?: boolean) => void;
  headerStyle?: string;
  headerContainerProps?: StackProps;
}

const Expandable = ({
  children,
  important,
  title,
  reverse,
  initializeOpened,
  lock,
  customHeader,
  onClick,
  value,
  classes,
  headerStyle,
  headerContainerProps = {},
  titleVariant = 'body1',
}: ExpandableProps) => {
  const [opened, setOpened] = useState(initializeOpened);
  const { classes: teceoClasses } = useTeceoStyles();
  const { classes: internalClasses } = useStyles();

  const isOpen = Boolean(onClick ? value : opened);

  const handleToggle = () => {
    if (lock) {
      return;
    }
    onClick?.(value);
    setOpened(!opened);
  };

  const titleClass =
    classes?.title ?? (important ? teceoClasses.upperTitle : internalClasses.lowerTitle);

  return (
    <Stack spacing={1}>
      <Box sx={{ pb: important && isOpen ? 1 : 0 }}>
        <Stack
          direction={reverse ? 'row-reverse' : 'row'}
          spacing={1}
          className={headerStyle}
          onClick={customHeader ? undefined : handleToggle}
          {...headerContainerProps}
          sx={[
            { alignItems: 'center' },
            ...(Array.isArray(headerContainerProps.sx)
              ? headerContainerProps.sx
              : [headerContainerProps.sx]),
          ]}
        >
          <Box sx={{ flexGrow: 1 }}>
            {customHeader ?? (
              <ExpandableTitle
                title={title}
                variant={titleVariant}
                className={titleClass}
                lock={lock}
              />
            )}
          </Box>

          {!lock && (
            <ExpandableToggle isOpen={isOpen} onClick={customHeader ? handleToggle : undefined} />
          )}
        </Stack>
      </Box>
      <Collapse in={isOpen} sx={{ width: '100%' }}>
        {children}
      </Collapse>
    </Stack>
  );
};

export default Expandable;
